import { Archive, Pencil } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Track } from "@/lib/api";

import { TrackArchiveDialog } from "./track-archive-dialog";
import { TrackFormDialog } from "./track-form-dialog";

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleDateString();
}

interface TrackTableProps {
  tracks: Track[];
}

export function TrackTable({ tracks }: TrackTableProps) {
  const [editTrack, setEditTrack] = useState<Track | null>(null);
  const [archiveTrack, setArchiveTrack] = useState<Track | null>(null);

  if (tracks.length === 0) {
    return <p className="text-muted-foreground py-8 text-center text-sm">No tracks found.</p>;
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary border-b">
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Business unit</th>
              <th className="px-4 py-3 text-left font-medium">Owner</th>
              <th className="px-4 py-3 text-left font-medium">Target date</th>
              <th className="px-4 py-3 text-left font-medium">Active</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tracks.map((track) => (
              <tr key={track.id} className="border-b last:border-0">
                <td className="px-4 py-3">{track.name}</td>
                <td className="text-muted-foreground px-4 py-3">{track.business_unit ?? "—"}</td>
                <td className="text-muted-foreground px-4 py-3">{track.owner_name ?? "—"}</td>
                <td className="text-muted-foreground px-4 py-3">{formatDate(track.target_date)}</td>
                <td className="px-4 py-3">
                  <Badge variant={track.is_active ? "secondary" : "outline"}>
                    {track.is_active ? "Active" : "Archived"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setEditTrack(track)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setArchiveTrack(track)}>
                      <Archive className="text-destructive h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TrackFormDialog
        track={editTrack}
        open={!!editTrack}
        onOpenChange={(open) => !open && setEditTrack(null)}
      />
      <TrackArchiveDialog
        track={archiveTrack}
        open={!!archiveTrack}
        onOpenChange={(open) => !open && setArchiveTrack(null)}
      />
    </>
  );
}
